import { Component, defineElement } from '@theme/component';
import { isWaitlistFormActive, isWaitlistOverrideActive } from '@theme/waitlist-state';
import {
  attentiveSubmissionKey,
  buildAttentivePayload,
  buildKlaviyoSubscriptionPayload,
  createMetaLeadTracker,
  createSubmissionId,
  normalizeWaitlistPhone,
  submitAttentiveSignup,
  submitKlaviyoSubscription,
} from '@theme/waitlist-submission';

function cloneTemplatePart(template, selector) {
  return template.content.querySelector(selector)?.content.cloneNode(true)
    || document.createDocumentFragment();
}

class ProductWaitlistForm extends Component {
  ready() {
    this.currentVariantId = this.dataset.currentVariantId || this.dataset.initialVariantId || '';
    this.currentFormTemplate = this.serverRenderedFormTemplate();
    this.submissionVersion = 0;
    this.submissionController = null;
    this.smsSubmissions = new Map();
    this.metaLeadTracker ||= createMetaLeadTracker();

    this.listen(this, 'submit', (event) => this.handleSubmit(event));
    this.listen(this, 'input', (event) => this.handleInput(event));

    this.setVariant(this.variantState(this.currentVariantId), { preserveValues: true });
  }

  /** Liquid renders the entry it resolved, so connection adopts that markup instead of rebuilding it. */
  serverRenderedFormTemplate() {
    const entry = this.dataset.initialEntry;
    const variantTemplate = this.activeTemplate;
    if (!entry || !variantTemplate) return null;

    return variantTemplate.content.querySelector(
      `template[data-waitlist-form][data-waitlist-entry="${CSS.escape(entry)}"]`
    );
  }

  teardown() {
    this.cancelSubmission();
    this.smsSubmissions?.clear();
  }

  get form() {
    return this.querySelector('form[data-waitlist-form]');
  }

  get activeTemplate() {
    return this.querySelector(
      `template[data-waitlist-variant="${CSS.escape(String(this.currentVariantId))}"]`
    );
  }

  variantState(variantId) {
    const template = this.querySelector(
      `template[data-waitlist-variant="${CSS.escape(String(variantId || ''))}"]`
    );
    if (!(template instanceof HTMLTemplateElement)) return null;

    return {
      id: template.dataset.waitlistVariant,
      available: template.dataset.variantAvailable === 'true',
      waitlistSchedule: template.dataset.waitlistSchedule || '',
    };
  }

  /** A variant ships one copy template per candidate waitlist entry. */
  resolveFormTemplate(variantTemplate, now = Date.now()) {
    const forms = [...variantTemplate.content.querySelectorAll('template[data-waitlist-form]')];
    const scheduled = forms.find((form) => isWaitlistOverrideActive({
      waitlistSchedule: [[form.dataset.waitlistStart || null, form.dataset.waitlistEnd || null]],
    }, now));

    return scheduled
      || forms.find((form) => !form.dataset.waitlistStart && !form.dataset.waitlistEnd)
      || null;
  }

  setVariant(variant, { preserveValues = false } = {}) {
    const variantId = variant?.id ? String(variant.id) : '';
    const template = this.querySelector(
      `template[data-waitlist-variant="${CSS.escape(variantId)}"]`
    );
    const resolvedVariant = template ? this.variantState(variantId) : null;
    const visible = Boolean(template && isWaitlistFormActive(resolvedVariant));
    const changed = variantId !== this.currentVariantId;

    if (changed) {
      this.cancelSubmission();
      this.currentVariantId = variantId;
    }

    this.dataset.currentVariantId = variantId;
    this.hidden = !visible;
    if (!(template instanceof HTMLTemplateElement)) return;

    const formTemplate = this.resolveFormTemplate(template);
    if (!formTemplate || formTemplate === this.currentFormTemplate) return;

    this.currentFormTemplate = formTemplate;
    this.applyTemplate(formTemplate, { preserveValues: preserveValues || !changed });
  }

  applyTemplate(formTemplate, { preserveValues = true } = {}) {
    const form = this.form;
    if (!form) return;

    const email = preserveValues ? form.elements.email?.value || '' : '';
    const phone = preserveValues ? form.querySelector('[data-waitlist-phone]')?.value || '' : '';

    this.querySelector('[data-waitlist-title]')?.replaceChildren(
      cloneTemplatePart(formTemplate, 'template[data-waitlist-title-template]')
    );
    this.querySelector('[data-waitlist-subtitle]')?.replaceChildren(
      cloneTemplatePart(formTemplate, 'template[data-waitlist-subtitle-template]')
    );
    this.querySelector('[data-waitlist-disclaimer]')?.replaceChildren(
      cloneTemplatePart(formTemplate, 'template[data-waitlist-disclaimer-template]')
    );
    this.querySelector('[data-waitlist-success]')?.replaceChildren(
      cloneTemplatePart(formTemplate, 'template[data-waitlist-success-template]')
    );

    form.dataset.listId = formTemplate.dataset.listId || '';
    form.dataset.source = formTemplate.dataset.source || '';
    form.dataset.variantId = this.currentVariantId || '';
    form.dataset.attentiveEnabled = formTemplate.dataset.attentiveEnabled || 'false';
    form.dataset.attentiveSignupSourceId = formTemplate.dataset.attentiveSignupSourceId || '';
    form.dataset.attentiveProfileAttribute = formTemplate.dataset.attentiveProfileAttribute || '';
    form.dataset.attentiveAlreadySubscribedMessage =
      formTemplate.dataset.attentiveAlreadySubscribedMessage || '';

    const listInput = form.elements.g;
    const variantInput = form.elements.variant;
    const emailInput = form.elements.email;
    const phoneInput = form.querySelector('[data-waitlist-phone]');
    const smsControls = form.querySelector('[data-waitlist-sms-controls]');
    const submit = form.querySelector('button[type="submit"]');
    const submitLabel = form.querySelector('[data-waitlist-submit-label]');

    if (listInput) listInput.value = form.dataset.listId;
    if (variantInput) variantInput.value = form.dataset.variantId;
    if (emailInput) {
      emailInput.value = email;
      emailInput.placeholder = formTemplate.dataset.emailPlaceholder || '';
    }
    if (phoneInput) {
      phoneInput.value = form.dataset.attentiveEnabled === 'true' ? phone : '';
      phoneInput.placeholder = formTemplate.dataset.phonePlaceholder || '';
      phoneInput.setCustomValidity('');
    }
    if (smsControls) smsControls.hidden = form.dataset.attentiveEnabled !== 'true';
    if (submit) {
      const ga4Params = JSON.stringify({
        CUSTOM_form_id: form.dataset.ga4FormId || form.id,
        CUSTOM_list_id: form.dataset.listId,
        CUSTOM_signup_source: form.dataset.source,
      });
      if (submit.dataset.ga4Params !== ga4Params) submit.dataset.ga4Params = ga4Params;
    }
    if (submitLabel) submitLabel.textContent = formTemplate.dataset.submitLabel || '';

    this.resetFeedback();
  }

  handleInput(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.matches('[data-waitlist-phone]')) {
      event.target.setCustomValidity('');
    }
  }

  cancelSubmission() {
    this.submissionVersion += 1;
    this.submissionController?.abort();
    this.submissionController = null;
    this.setBusy(false);
  }

  resetFeedback() {
    const form = this.form;
    if (!form) return;

    form.querySelector('[data-waitlist-fields]')?.removeAttribute('hidden');
    form.querySelector('[data-waitlist-disclaimer]')?.removeAttribute('hidden');
    form.querySelector('[data-waitlist-success]')?.setAttribute('hidden', '');
    form.querySelector('[data-waitlist-error]')?.setAttribute('hidden', '');
    const status = form.querySelector('[data-waitlist-status]');
    if (status) status.textContent = '';
  }

  setBusy(busy) {
    const form = this.form;
    const submit = form?.querySelector('button[type="submit"]');
    if (!form || !submit) return;

    form.toggleAttribute('data-submitting', busy);
    submit.disabled = busy;
    if (busy) {
      submit.setAttribute('aria-busy', 'true');
      const label = submit.querySelector('[data-waitlist-submit-label]')?.textContent.trim();
      if (label) submit.setAttribute('aria-label', label);
    } else {
      submit.removeAttribute('aria-busy');
      submit.removeAttribute('aria-label');
    }
  }

  showError() {
    const error = this.form?.querySelector('[data-waitlist-error]');
    if (error) error.hidden = false;
  }

  showSuccess() {
    const form = this.form;
    if (!form) return;

    form.querySelector('[data-waitlist-fields]')?.setAttribute('hidden', '');
    form.querySelector('[data-waitlist-disclaimer]')?.setAttribute('hidden', '');
    form.querySelector('[data-waitlist-error]')?.setAttribute('hidden', '');
    const success = form.querySelector('[data-waitlist-success]');
    if (success) {
      success.hidden = false;
      success.focus({ preventScroll: true });
    }
  }

  validatePhone(form) {
    const phoneInput = form.querySelector('[data-waitlist-phone]');
    if (!phoneInput || !phoneInput.value.trim()) return '';

    const normalizedPhone = normalizeWaitlistPhone(phoneInput.value, {
      usOnly: this.dataset.phoneUsOnly !== 'false',
    });
    if (!normalizedPhone) {
      phoneInput.setCustomValidity(this.dataset.phoneInvalid || '');
      phoneInput.reportValidity();
      return null;
    }

    phoneInput.setCustomValidity('');
    return normalizedPhone;
  }

  submitSms(form, email, normalizedPhone, controller) {
    const signupSourceId = form.dataset.attentiveSignupSourceId || '';
    const payload = buildAttentivePayload({
      phoneNumber: normalizedPhone,
      email,
      formId: form.id,
      source: form.dataset.source,
      pageLocation: window.location.href,
      signupSourceId,
      profileAttribute: form.dataset.attentiveProfileAttribute,
      alreadySubscribedMessage: form.dataset.attentiveAlreadySubscribedMessage,
      // Entering a phone number under the visible disclaimer is the SMS consent.
      smsConsent: true,
      submissionId: createSubmissionId(),
    });
    const submissionKey = attentiveSubmissionKey(payload);
    const existingSubmission = this.smsSubmissions.get(submissionKey);
    if (existingSubmission) return existingSubmission;

    const submission = submitAttentiveSignup({
      payload,
      signal: controller.signal,
    }).catch(() => {
      if (this.smsSubmissions.get(submissionKey) === submission) {
        this.smsSubmissions.delete(submissionKey);
      }
      return { ok: false };
    });

    this.smsSubmissions.set(submissionKey, submission);
    return submission;
  }

  requestMetaLead() {
    this.metaLeadTracker?.request({
      enabled: this.dataset.metaLeadEnabled === 'true',
      pixelId: this.dataset.metaPixelId,
      pageLocation: window.location.href,
      customerPrivacy: window.Shopify?.customerPrivacy,
    });
  }

  async handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-waitlist-form]')) return;
    event.preventDefault();

    if (this.submissionController) return;
    if (!form.reportValidity()) return;

    const normalizedPhone = this.validatePhone(form);
    if (normalizedPhone === null) return;

    const email = form.elements.email?.value.trim() || '';
    const publicKey = this.dataset.klaviyoPublicKey || '';
    const listId = form.dataset.listId || '';
    if (!email || !publicKey || !listId) {
      this.showError();
      return;
    }

    this.resetFeedback();
    this.setBusy(true);
    const controller = new AbortController();
    this.submissionController = controller;
    const submissionVersion = ++this.submissionVersion;
    const parameters = new URLSearchParams(window.location.search);

    const klaviyoPayload = buildKlaviyoSubscriptionPayload({
      email,
      listId,
      source: form.dataset.source,
      productId: this.dataset.productId,
      variantId: form.dataset.variantId,
      utmSource: parameters.get('utm_source'),
      utmCampaign: parameters.get('utm_campaign'),
    });

    const smsSubmission = normalizedPhone && form.dataset.attentiveEnabled === 'true'
      ? this.submitSms(form, email, normalizedPhone, controller)
      : Promise.resolve({ ok: true, skipped: true });

    try {
      await submitKlaviyoSubscription({
        publicKey,
        payload: klaviyoPayload,
        signal: controller.signal,
      });
      if (submissionVersion !== this.submissionVersion) return;

      this.showSuccess();
      this.requestMetaLead();
      void smsSubmission;
    } catch (_error) {
      if (!controller.signal.aborted && submissionVersion === this.submissionVersion) {
        this.showError();
      }
    } finally {
      if (submissionVersion === this.submissionVersion) {
        this.submissionController = null;
        this.setBusy(false);
      }
    }
  }
}

defineElement('product-waitlist-form', ProductWaitlistForm);
